
'use client';

import { useState } from 'react';
import { Link } from 'react-router-dom';

const blogPosts = [
  {
    id: 1,
    title: "5 Natural Ways to Boost Your Immunity This Winter",
    excerpt: "Discover powerful Ayurvedic herbs and practices that can strengthen your immune system naturally. From turmeric golden milk to daily pranayama breathing exercises.",
    category: "Health Tips",
    author: "Dr. Priya Sharma",
    date: "December 15, 2024",
    readTime: "8 min read",
    image: "https://readdy.ai/api/search-image?query=Ayurvedic%20immunity%20boosting%20herbs%20turmeric%20ginger%20amla%20fresh%20ingredients%20on%20wooden%20table%20natural%20lighting%20healthy%20lifestyle%20clean%20background%20professional%20photography&width=600&height=400&seq=blog1&orientation=landscape",
    featured: true
  },
  {
    id: 2,
    title: "The Complete Guide to Turmeric: Nature's Golden Healer",
    excerpt: "Learn about the incredible healing properties of turmeric, from reducing inflammation to supporting joint health. Plus, discover the best ways to incorporate it into your daily routine.",
    category: "Ingredients",
    author: "Ayush Patel",
    date: "December 12, 2024",
    readTime: "12 min read",
    image: "https://readdy.ai/api/search-image?query=Fresh%20turmeric%20root%20and%20powder%20golden%20yellow%20spice%20curcumin%20healing%20properties%20wooden%20spoon%20natural%20ingredients%20clean%20white%20background%20professional%20food%20photography&width=600&height=400&seq=blog2&orientation=landscape"
  },
  {
    id: 3,
    title: "Ayurvedic Morning Routine for Optimal Wellness",
    excerpt: "Start your day the Ayurvedic way with this comprehensive morning routine. From oil pulling to meditation, learn practices that will transform your health.",
    category: "Wellness",
    author: "Dr. Anita Mehta",
    date: "December 10, 2024",
    readTime: "10 min read",
    image: "https://readdy.ai/api/search-image?query=Peaceful%20morning%20meditation%20yoga%20mat%20sunrise%20natural%20lighting%20wellness%20routine%20healthy%20lifestyle%20plants%20water%20bottle%20journals%20clean%20minimalist%20background&width=600&height=400&seq=blog3&orientation=landscape"
  },
  {
    id: 4,
    title: "Natural Skincare Recipes with Ayurvedic Herbs",
    excerpt: "Create your own natural skincare products using traditional Ayurvedic ingredients. Simple recipes for glowing, healthy skin using neem, rose water, and sandalwood.",
    category: "DIY Recipes",
    author: "Kavya Singh",
    date: "December 8, 2024",
    readTime: "15 min read",
    image: "https://readdy.ai/api/search-image?query=Natural%20skincare%20ingredients%20neem%20leaves%20rose%20petals%20sandalwood%20powder%20clay%20mask%20bowls%20wooden%20spoons%20organic%20beauty%20products%20clean%20white%20background&width=600&height=400&seq=blog4&orientation=landscape"
  },
  {
    id: 5,
    title: "Understanding Your Dosha: A Beginner's Guide",
    excerpt: "Discover your unique Ayurvedic constitution and learn how to live in harmony with your dosha. Personalized tips for Vata, Pitta, and Kapha body types.",
    category: "Ayurveda Basics",
    author: "Dr. Rajesh Kumar",
    date: "December 5, 2024",
    readTime: "20 min read",
    image: "https://readdy.ai/api/search-image?query=Three%20dosha%20symbols%20Vata%20Pitta%20Kapha%20ayurvedic%20constitution%20wheel%20ancient%20symbols%20natural%20elements%20earth%20fire%20air%20water%20balanced%20lifestyle&width=600&height=400&seq=blog5&orientation=landscape"
  },
  {
    id: 6,
    title: "Ashwagandha: The Ultimate Stress-Busting Herb",
    excerpt: "Learn how this powerful adaptogen can help manage stress, improve sleep, and boost energy levels. Everything you need to know about incorporating ashwagandha safely.",
    category: "Ingredients",
    author: "Dr. Sunita Rao",
    date: "December 2, 2024",
    readTime: "9 min read",
    image: "https://readdy.ai/api/search-image?query=Ashwagandha%20root%20and%20powder%20stress%20relief%20adaptogen%20herb%20natural%20medicine%20wooden%20bowl%20dried%20herbs%20clean%20background%20professional%20photography&width=600&height=400&seq=blog6&orientation=landscape"
  }
];

const categories = ["All", "Health Tips", "Ingredients", "Wellness", "DIY Recipes", "Ayurveda Basics"];

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = blogPosts.find(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured || selectedCategory !== "All" || searchQuery);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-green-700 to-green-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Wellness Blog</h1>
            <p className="text-xl text-green-100 max-w-2xl mx-auto">
              Discover ancient Ayurvedic wisdom for modern health and wellness
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-3/4">
            {selectedCategory === "All" && !searchQuery && featuredPost && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold mb-6 text-gray-800">Featured Article</h2>
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                  <div className="md:flex">
                    <div className="md:w-1/2">
                      <img 
                        src={featuredPost.image}
                        alt={featuredPost.title}
                        className="w-full h-64 md:h-full object-cover"
                      />
                    </div>
                    <div className="md:w-1/2 p-8">
                      <div className="flex items-center mb-4">
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                          {featuredPost.category}
                        </span>
                        <span className="text-gray-500 text-sm ml-4">{featuredPost.readTime}</span>
                      </div>
                      <h3 className="text-2xl font-bold mb-3 text-gray-800">{featuredPost.title}</h3>
                      <p className="text-gray-600 mb-4">{featuredPost.excerpt}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center mr-3">
                            <span className="text-green-800 font-semibold text-sm">
                              {featuredPost.author.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{featuredPost.author}</p>
                            <p className="text-sm text-gray-500">{featuredPost.date}</p>
                          </div>
                        </div>
                        <button className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap">
                          Read More
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-8">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Latest Articles</h2>
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search articles..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <i className="ri-search-line absolute left-3 top-3 text-gray-400"></i>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {regularPosts.map((post) => (
                <article key={post.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <img 
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <div className="flex items-center mb-3">
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                        {post.category}
                      </span>
                      <span className="text-gray-500 text-sm ml-4">{post.readTime}</span>
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-gray-800 hover:text-green-600 cursor-pointer">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center mr-2">
                          <span className="text-green-800 font-semibold text-xs">
                            {post.author.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{post.author}</p>
                          <p className="text-xs text-gray-500">{post.date}</p>
                        </div>
                      </div>
                      <button className="text-green-600 hover:text-green-700 font-medium text-sm whitespace-nowrap">
                        Read More →
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-search-line text-4xl text-gray-400 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No articles found</h3>
                <p className="text-gray-500">Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>

          <div className="lg:w-1/4">
            <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
              <h3 className="text-lg font-bold mb-4 text-gray-800">Categories</h3>
              <div className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                      selectedCategory === category
                        ? 'bg-green-100 text-green-800 font-medium'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-green-50 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-4 text-gray-800">Newsletter</h3>
              <p className="text-gray-600 mb-4">Get weekly wellness tips and exclusive Ayurvedic insights delivered to your inbox.</p>
              <div className="space-y-3">
                <input
                  type="email"
                  placeholder="Your email address"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <button className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
